/**
 * Shop Page Component
 * Lists all products with search and filtering capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FadeInUp } from './MotionWrappers';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/psearch.css';
import '../assets/css/shop.css';
import TryOn from './TryOn';
import SEO from './SEO';
import { supabase } from '../supabaseClient';
import Nav from './Nav';
import SignInModal from './SignInModal';
import { useCurrency } from '../context/CurrencyContext';
import apiClient from '../config/config';

function Shop() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currency, getCurrencySymbol } = useCurrency();
  
  // Search and filter state
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('newest');
  
  // UI state
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [pendingTryOnImage, setPendingTryOnImage] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [savedProducts, setSavedProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [priceOpen, setPriceOpen] = useState(true);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Normalize product ID
  const normalizeProductId = useCallback((productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  }, []);

  // Capitalize title helper
  const capitalizeTitle = useCallback((title) => {
    if (!title) return '';
    const lowerCaseWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'as'];
    return title
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        if (index === 0 || !lowerCaseWords.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word;
      })
      .join(' ');
  }, []);

  // Check for user authentication
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Load saved products
  useEffect(() => {
    if (user) {
      const loadSavedProducts = async () => {
        try {
          const { data, error } = await supabase
            .from('saved_products')
            .select('product_id')
            .eq('user_id', user.id);
          
          if (!error && data) {
            setSavedProducts(data.map(item => item.product_id));
          }
        } catch (err) {
          console.error('Error loading saved products:', err);
        }
      };
      loadSavedProducts();
    }
  }, [user]);

  // Extract unique brands and categories from products
  useEffect(() => {
    if (products.length > 0) {
      const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
      const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
      setBrands(uniqueBrands.sort());
      setCategories(uniqueCategories.sort());
    }
  }, [products]);

  // Fetch products
  const fetchProducts = useCallback(async (searchQuery = '', category = 'All', brand = '', minPrice = '', maxPrice = '', sort = 'newest') => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        q: searchQuery,
        category: category === 'All' ? '' : category,
        brand: brand,
        currency: currency,
        sort: sort
      });

      const response = await apiClient.get(`/products/search/?${params}`);
      const data = response.data;
      
      let filteredProducts = Array.isArray(data) ? data : [];
      
      // Client-side price filtering
      if (minPrice || maxPrice) {
        filteredProducts = filteredProducts.filter(product => {
          const price = product.price || 0;
          if (minPrice && price < parseFloat(minPrice)) return false;
          if (maxPrice && price > parseFloat(maxPrice)) return false;
          return true;
        });
      }

      // Client-side sorting (default preserves backend order: new brands first + equal visibility)
      if (sort === 'price-low') {
        filteredProducts.sort((a, b) => (a.price || 0) - (b.price || 0));
      } else if (sort === 'price-high') {
        filteredProducts.sort((a, b) => (b.price || 0) - (a.price || 0));
      } else if (sort === 'name') {
        filteredProducts.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      }
      // else: keep backend ordering (new brand boost + round-robin equal visibility)

      setProducts(filteredProducts);
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  // Initial load
  useEffect(() => {
    fetchProducts(query, selectedCategory, selectedBrand, priceRange.min, priceRange.max, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProducts]);

  // Update query when URL search params change (from Nav search)
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    if (urlQuery && urlQuery !== query) {
      setQuery(urlQuery);
    }
  }, [searchParams]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'category':
        setSelectedCategory(value);
        break;
      case 'brand':
        setSelectedBrand(value);
        break;
      case 'priceMin':
        setPriceRange(prev => ({ ...prev, min: value }));
        break;
      case 'priceMax':
        setPriceRange(prev => ({ ...prev, max: value }));
        break;
      case 'sort':
        setSortBy(value);
        break;
      default:
        break;
    }
  };

  // Apply filters (including search query changes via debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(query, selectedCategory, selectedBrand, priceRange.min, priceRange.max, sortBy);
    }, query ? 500 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedCategory, selectedBrand, priceRange, sortBy, fetchProducts]);

  // Handle try-on
  const handleTryOn = (image) => {
    if (!user) {
      setSignInModalMessage('Sign in to try it on and save your look.');
      setPendingTryOnImage(image);
      setShowSignInModal(true);
      return;
    }
    setTryOnImage(image);
    setTryOnOpen(true);
  };

  // Handle save product
  const handleSaveProduct = async (productId) => {
    if (!user) {
      setSignInModalMessage('Sign in to save products to your collection.');
      setShowSignInModal(true);
      return;
    }

    const isSaved = savedProducts.includes(productId);
    
    try {
      if (isSaved) {
        await supabase
          .from('saved_products')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId);
        setSavedProducts(prev => prev.filter(id => id !== productId));
      } else {
        await supabase
          .from('saved_products')
          .insert({ user_id: user.id, product_id: productId });
        setSavedProducts(prev => [...prev, productId]);
      }
    } catch (err) {
      console.error('Error saving product:', err);
    }
  };

  // Handle sign-in modal close
  const handleSignInModalClose = () => {
    setShowSignInModal(false);
    setSignInModalMessage('');
    setPendingTryOnImage(null);
  };


  return (
    <div className="container py-5 font-raleway">
      <SEO
        title="Shop Kashmiri Fashion"
        description="Browse and shop authentic Kashmiri clothing online. Filter by category, brand, and price. Pashmina shawls, phiran, aari embroidery, and more from Kashmir's finest artisans."
        path="/shop"
      />
      <Nav />
      
      {/* Hero Header */}
      <div className="shop-hero page-hero-fullwidth" style={{
        background: 'linear-gradient(135deg, rgba(248,248,248,0.8) 0%, rgba(255,255,255,0.6) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        paddingTop: '60px',
        paddingBottom: '50px',
        position: 'relative',
        overflow: 'hidden',
      }}>
          <div className="fluid-blob fluid-blob-2" style={{ opacity: 0.04 }} />
          <FadeInUp delay={0.1}>
          <h1 className="shop-title" style={{
            fontSize: '3rem',
            fontWeight: 600,
            fontFamily: "'Montserrat', sans-serif",
            color: '#1a1a1a',
            marginBottom: '16px',
            letterSpacing: '0em',
            position: 'relative',
            zIndex: 1,
          }}>S H O P</h1>
          </FadeInUp>
          <FadeInUp delay={0.25}>
          <p style={{
            fontSize: '1rem',
            fontFamily: "'Montserrat', sans-serif",
            color: '#666',
            maxWidth: '560px',
            lineHeight: 1.6,
            margin: 0,
            position: 'relative',
            zIndex: 1,
          }}>
            Explore the complete collection of handcrafted Kashmiri fashion pieces from the finest artisan brands.
          </p>
          </FadeInUp>
      </div>

      <Container className="mt-0">
        {/* Search Bar */}
        <div style={{
          padding: '24px 0',
          borderBottom: '1px solid #e5e5e5',
          marginBottom: '24px',
        }}>
          <div style={{ position: 'relative', maxWidth: '600px' }}>
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="none" stroke="#999" strokeWidth="2"
              style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Form.Control
              type="search"
              placeholder="Search products or brands..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                border: '1px solid #d1d1d1',
                borderRadius: '6px',
                padding: '10px 14px 10px 42px',
                fontSize: '14px',
                background: '#fff',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '40px' }}>

          {/* Left Sidebar Filters */}
          <div className="shop-sidebar" style={{
            width: '220px',
            flexShrink: 0,
            borderRight: '1px solid #e5e5e5',
            paddingRight: '32px',
          }}>
            {/* Clear All */}
            <button
              onClick={() => {
                setQuery('');
                setSelectedCategory('All');
                setSelectedBrand('');
                setPriceRange({ min: '', max: '' });
                setSortBy('newest');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: '0 0 20px 0',
                fontSize: '14px',
                color: '#1a1a1a',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              Clear All
            </button>

            <div style={{ borderTop: '1px solid #e5e5e5' }} />

            {/* Category Section */}
            <div style={{ paddingTop: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e5e5' }}>
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                Category
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#1a1a1a" strokeWidth="2"
                  style={{
                    transition: 'transform 0.2s',
                    transform: categoryOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {categoryOpen && (
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <button
                    onClick={() => handleFilterChange('category', 'All')}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '6px 0',
                      fontSize: '14px',
                      color: selectedCategory === 'All' ? '#1a1a1a' : '#666',
                      fontWeight: selectedCategory === 'All' ? '600' : '400',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'Montserrat, sans-serif',
                      transition: 'color 0.2s',
                    }}
                  >
                    All
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => handleFilterChange('category', cat)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '6px 0',
                        fontSize: '14px',
                        color: selectedCategory === cat ? '#1a1a1a' : '#666',
                        fontWeight: selectedCategory === cat ? '600' : '400',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'Montserrat, sans-serif',
                        transition: 'color 0.2s',
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Price Section */}
            <div style={{ paddingTop: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e5e5' }}>
              <button
                onClick={() => setPriceOpen(!priceOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                Price
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#1a1a1a" strokeWidth="2"
                  style={{
                    transition: 'transform 0.2s',
                    transform: priceOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {priceOpen && (
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {[
                    { value: '', label: 'All Prices' },
                    { value: '0-50', label: `Under ${getCurrencySymbol(currency)}50` },
                    { value: '50-100', label: `${getCurrencySymbol(currency)}50 – ${getCurrencySymbol(currency)}100` },
                    { value: '100-200', label: `${getCurrencySymbol(currency)}100 – ${getCurrencySymbol(currency)}200` },
                    { value: '200-500', label: `${getCurrencySymbol(currency)}200 – ${getCurrencySymbol(currency)}500` },
                    { value: '500-', label: `Over ${getCurrencySymbol(currency)}500` },
                  ].map(option => {
                    const currentVal = priceRange.min === '' && priceRange.max === '' ? '' : `${priceRange.min}-${priceRange.max}`;
                    const isActive = currentVal === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (!option.value) {
                            setPriceRange({ min: '', max: '' });
                          } else {
                            const [min, max] = option.value.split('-');
                            setPriceRange({ min, max });
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '6px 0',
                          fontSize: '14px',
                          color: isActive ? '#1a1a1a' : '#666',
                          fontWeight: isActive ? '600' : '400',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'Montserrat, sans-serif',
                          transition: 'color 0.2s',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Content Area */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Top Bar: Product count + Sort */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: '24px',
              borderBottom: '1px solid #e5e5e5',
              marginBottom: '24px',
            }}>
              {/* Product count */}
              <div style={{ fontSize: '14px', color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
                {!loading && !error && products.length > 0 && (
                  <span>Showing all {products.length} result{products.length !== 1 ? 's' : ''}</span>
                )}
              </div>

              {/* Sort by dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#1a1a1a',
                    fontFamily: 'Montserrat, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Sort by: <strong>{
                    sortBy === 'newest' ? 'Default' :
                    sortBy === 'price-low' ? 'Price (low to high)' :
                    sortBy === 'price-high' ? 'Price (high to low)' :
                    sortBy === 'name' ? 'Name' : 'Default'
                  }</strong>
                  <svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="#1a1a1a" strokeWidth="2"
                    style={{
                      transition: 'transform 0.2s',
                      transform: showSortDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <>
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 998 }}
                      onClick={() => setShowSortDropdown(false)}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      background: '#fff',
                      border: '1px solid #e5e5e5',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      zIndex: 999,
                      minWidth: '200px',
                    }}>
                      {[
                        { value: 'newest', label: 'Default' },
                        { value: 'price-low', label: 'Price (low to high)' },
                        { value: 'price-high', label: 'Price (high to low)' },
                        { value: 'name', label: 'Name' },
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            handleFilterChange('sort', option.value);
                            setShowSortDropdown(false);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '10px 16px',
                            background: 'none',
                            border: 'none',
                            fontSize: '14px',
                            color: sortBy === option.value ? '#1a1a1a' : '#666',
                            fontWeight: sortBy === option.value ? '600' : '400',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'Montserrat, sans-serif',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f8f8'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading products...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="error-state">
                <p>{error}</p>
                <Button variant="dark" onClick={() => fetchProducts()}>
                  Try Again
                </Button>
              </div>
            )}

            {/* Products Grid */}
            {!loading && !error && (
              <>
                {products.length === 0 ? (
                  <div className="empty-state">
                    <p>No products found matching your criteria.</p>
                    <Button variant="dark" onClick={() => {
                      setQuery('');
                      setSelectedCategory('All');
                      setSelectedBrand('');
                      setPriceRange({ min: '', max: '' });
                    }}>
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  <Row style={{ display: 'flex', gap: '0' }}>
                    {products.map((product, index) => (
                      <Col md={4} key={product.id} style={{ padding: '0 12px', marginBottom: '24px' }}>
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: '-30px' }}
                          transition={{ duration: 0.5, delay: (index % 3) * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                          whileHover={{ y: -6 }}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/product/${product.id}`)}
                        >
                          {/* Image */}
                          <div style={{
                            width: '100%',
                            height: '480px',
                            overflow: 'hidden',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                          }}>
                            <img
                              src={product.image || '/default-image.avif'}
                              alt={product.title || 'Product Image'}
                              loading="lazy"
                              decoding="async"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center top',
                                transition: 'transform 0.3s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.03)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/default-image.avif';
                              }}
                            />

                            {/* Heart Save Icon */}
                            <button
                              style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                background: 'rgba(255,255,255,0.95)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 10,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s',
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSaveProduct(product.id);
                              }}
                              title={savedProducts.includes(normalizeProductId(product.id)) ? 'Remove from saved' : 'Save product'}
                            >
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill={savedProducts.includes(normalizeProductId(product.id)) ? '#e63946' : 'none'}
                                stroke="#e63946"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                              </svg>
                            </button>

                            {/* Try it on Button */}
                            <button
                              style={{
                                position: 'absolute',
                                bottom: '12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                padding: '8px 14px',
                                borderRadius: '999px',
                                border: 'none',
                                background: 'rgba(0,0,0,0.7)',
                                color: '#fff',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                backdropFilter: 'blur(6px)',
                              }}
                              onClick={(e) => { e.stopPropagation(); handleTryOn(product.image); }}
                            >
                              Try it on
                            </button>
                          </div>

                          {/* Product Info */}
                          <div style={{ paddingTop: '14px' }}>
                            <h3 style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              fontFamily: 'Montserrat, sans-serif',
                              color: '#1a1a1a',
                              margin: '0 0 4px 0',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              {product.brand || 'Unknown Brand'}
                            </h3>
                            <p style={{
                              fontSize: '13px',
                              fontFamily: 'Montserrat, sans-serif',
                              color: '#888',
                              margin: '0 0 4px 0',
                            }}>
                              {capitalizeTitle(product.title) || 'Unnamed Product'}
                            </p>
                            <p style={{
                              fontSize: '13px',
                              fontFamily: 'Montserrat, sans-serif',
                              color: '#888',
                              margin: 0,
                            }}>
                              {getCurrencySymbol(currency)} {product.price || 'Not Available'}
                            </p>
                          </div>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            )}
          </div>
        </div>
      </Container>

      {/* Try On Modal */}
      {tryOnOpen && user && (
        <TryOn
          productImage={tryOnImage}
          onClose={() => setTryOnOpen(false)}
          user={user}
        />
      )}

      {/* Sign In Modal */}
      <SignInModal
        open={showSignInModal}
        onClose={handleSignInModalClose}
        message={signInModalMessage}
      />

      <style>{`
        @media (max-width: 768px) {
          .shop-hero {
            padding: 40px 16px 30px !important;
          }
          .shop-title {
            font-size: 2rem !important;
          }
          .shop-sidebar {
            display: none !important;
          }
        }
        @media (max-width: 480px) {
          .shop-title {
            font-size: 1.5rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Shop;
